import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  getMaintenanceDripSettings,
  updateMaintenanceDripSettings,
  getMaintenanceDripSends,
  runMaintenanceDripNow,
  drillMaintenanceDripNow,
  previewMaintenanceDripEmail,
} from '../lib/api';
import { optionalEmailListOrNull, optionalEmailOrNull, safeToastError } from '../lib/apiErrors';
import { isExistingInvoiceReuseSkip } from '../lib/maintenanceCampaignUi';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const inputCls = 'w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm focus:border-gray-400 focus:outline-none';
const areaCls = 'w-full rounded-md border border-gray-200 bg-white px-2.5 py-2 text-sm font-mono text-[12px] leading-relaxed text-gray-900 shadow-sm focus:border-gray-400 focus:outline-none';

/** @typedef {'prep-30' | 'prep-7' | 'overdue-1' | 'overdue-2' | 'overdue-3'} EditableStepKey */

const OVERDUE_META = {
  1: { key: 'overdue-1', short: 'Friendly', flow: 'Friendly (+7d)', blurb: 'Friendly reminder — soft tone, no escalation.' },
  2: { key: 'overdue-2', short: 'Second', flow: 'Second (+14d)', blurb: 'Still outstanding — ask customers to reply if paid or if there is an issue.' },
  3: { key: 'overdue-3', short: 'Final', flow: 'Final (+21d)', blurb: 'Final client reminder — ask for a reply if there is an issue. Triggers one internal P&L follow-up (no 4th email).' },
};

const FLOW_NODES = [
  { id: 'prep-30', label: '30 days before fee', kind: 'email', stepKey: 'prep-30' },
  { id: 'prep-7', label: '7 days before fee', kind: 'email', stepKey: 'prep-7' },
  { id: 'manual-invoice', label: 'Launch invoice & reminders', kind: 'manual' },
  { id: 'due-date', label: 'Due date', kind: 'milestone' },
  { id: 'overdue-1', label: OVERDUE_META[1].flow, kind: 'email', stepKey: 'overdue-1' },
  { id: 'overdue-2', label: OVERDUE_META[2].flow, kind: 'email', stepKey: 'overdue-2' },
  { id: 'overdue-3', label: OVERDUE_META[3].flow, kind: 'email', stepKey: 'overdue-3' },
  { id: 'paid-stop', label: 'Paid stops reminders', kind: 'stop' },
];

function Section({ title, description, children, className = '' }) {
  return (
    <section className={cn('rounded-xl border border-gray-200 bg-white p-4 sm:p-5', className)}>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children, className = '', hint = null }) {
  return (
    <label className={cn('block text-xs font-medium text-gray-600', className)}>
      {label}
      <div className="mt-1">{children}</div>
      {hint ? <p className="mt-1 text-[11px] font-normal text-gray-500">{hint}</p> : null}
    </label>
  );
}

function LangTabs({ value, onChange }) {
  return (
    <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
      {['en', 'fr'].map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => onChange(lang)}
          className={cn(
            'rounded-md px-2.5 py-1 text-[11px] font-semibold uppercase',
            value === lang ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800',
          )}
        >
          {lang}
        </button>
      ))}
    </div>
  );
}

function prepIndexForDays(settings, days) {
  const steps = settings?.prep_steps || [];
  const idx = steps.findIndex((s) => Number(s.days_before_next_fee) === days);
  return idx >= 0 ? idx : (days === 7 ? 1 : 0);
}

function overdueIndexFromKey(stepKey) {
  if (stepKey === 'overdue-2') return 2;
  if (stepKey === 'overdue-3') return 3;
  return 1;
}

function ensureOverdueReminders(settings) {
  const existing = Array.isArray(settings?.overdue?.reminders) ? settings.overdue.reminders : [];
  const byIdx = {};
  existing.forEach((r) => {
    const i = Number(r?.index);
    if (i >= 1 && i <= 3 && !byIdx[i]) byIdx[i] = r;
  });
  const reminders = [1, 2, 3].map((i) => {
    const cur = byIdx[i] || {};
    return {
      index: i,
      enabled: cur.enabled !== false,
      subject_en: cur.subject_en || '',
      body_en: cur.body_en || '',
      subject_fr: cur.subject_fr || '',
      body_fr: cur.body_fr || '',
    };
  });
  return {
    ...settings.overdue,
    interval_days: 7,
    max_reminders: 3,
    reminders,
  };
}

function stepMeta(stepKey, settings) {
  if (stepKey === 'prep-30' || stepKey === 'prep-7') {
    const days = stepKey === 'prep-7' ? 7 : 30;
    const idx = prepIndexForDays(settings, days);
    const step = settings?.prep_steps?.[idx];
    return {
      title: days === 7 ? '7 days before fee' : '30 days before fee',
      blurb: 'Advance notice only — no invoice, no payment request.',
      phase: 'prep',
      stepIndex: idx,
      enabled: step?.enabled !== false,
      value: step || {},
      overdueIndex: null,
    };
  }
  const overdueIndex = overdueIndexFromKey(stepKey);
  const ov = ensureOverdueReminders(settings);
  const step = (ov.reminders || []).find((r) => Number(r.index) === overdueIndex) || ov.reminders[overdueIndex - 1] || {};
  const labels = OVERDUE_META[overdueIndex];
  return {
    title: `${labels.short} reminder`,
    blurb: labels.blurb,
    phase: 'overdue',
    stepIndex: overdueIndex - 1,
    enabled: step?.enabled !== false && settings?.overdue?.enabled !== false,
    value: step,
    overdueIndex,
    overdueMasterEnabled: settings?.overdue?.enabled !== false,
  };
}

function isDrillSend(row) {
  const id = String(row?.id || '');
  const step = String(row?.step_key || '');
  return id.startsWith('drill:') || step.startsWith('drill:') || step.startsWith('sample:');
}

function JourneyFlow({ selectedStepKey, onSelectStep, dripEnabled }) {
  return (
    <div className="space-y-3">
      <div className="-mx-1 overflow-x-auto px-1 pb-2">
        <ol className="flex w-max min-w-full items-stretch gap-1.5 pr-3">
          {FLOW_NODES.map((node, i) => {
            const isEmail = node.kind === 'email';
            const isSelected = isEmail && node.stepKey === selectedStepKey;
            const chipClass = cn(
              'flex h-full w-[7.25rem] shrink-0 flex-col justify-center rounded-lg border px-2 py-2 text-left sm:w-[8rem]',
              node.kind === 'manual' && 'border-amber-300 bg-amber-50',
              node.kind === 'milestone' && 'border-gray-200 bg-gray-50',
              node.kind === 'stop' && 'border-emerald-300 bg-emerald-50',
              isEmail && !isSelected && 'border-gray-200 bg-white hover:border-gray-400',
              isEmail && isSelected && 'border-slate-800 bg-slate-900 text-white shadow-sm',
            );
            const chip = (
              <div className={chipClass}>
                <span
                  className={cn(
                    'text-[10px] font-semibold uppercase tracking-wide',
                    isSelected ? 'text-slate-300' : 'text-gray-400',
                    node.kind === 'manual' && 'text-amber-700',
                    node.kind === 'stop' && 'text-emerald-700',
                  )}
                >
                  {node.kind === 'email' ? 'Auto email' : node.kind === 'manual' ? 'Outside drip' : node.kind === 'stop' ? 'Stop' : 'Milestone'}
                </span>
                <span className={cn('mt-0.5 text-[11px] font-medium leading-snug sm:text-[12px]', isSelected ? 'text-white' : 'text-gray-900')}>
                  {node.label}
                </span>
              </div>
            );

            return (
              <li key={node.id} className="flex shrink-0 items-center gap-1.5">
                {isEmail ? (
                  <button
                    type="button"
                    onClick={() => onSelectStep(node.stepKey)}
                    className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-1"
                    aria-pressed={isSelected}
                  >
                    {chip}
                  </button>
                ) : (
                  chip
                )}
                {i < FLOW_NODES.length - 1 ? (
                  <span className="shrink-0 text-gray-300" aria-hidden>
                    →
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
      </div>
      <div className="flex flex-col gap-1.5 text-[12px] text-gray-600 sm:flex-row sm:flex-wrap sm:gap-x-4">
        <p>
          For each maintenance line, enter the recipient email(s), then use{' '}
          <span className="font-medium text-amber-800">Launch invoice &amp; reminders</span>
          . Launch creates, links and downloads the Bexio invoice; the invoice PDF is emailed on
          the fee date, or immediately when launched on/after the fee date. It starts that
          line&apos;s future reminder schedule.
        </p>
        <p>
          <span className="font-medium text-emerald-800">Paid</span>
          {' '}(manual or Bexio) stops future reminders and clears the Final follow-up notification. Zero balance alone does not.
        </p>
        {!dripEnabled ? (
          <p className="font-medium text-amber-700">Drip automation is currently disabled.</p>
        ) : null}
      </div>
    </div>
  );
}

export function MaintenanceDrippingTab() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [settings, setSettings] = useState(null);
  const [sends, setSends] = useState([]);
  /** @type {[EditableStepKey, function]} */
  const [selectedStepKey, setSelectedStepKey] = useState('prep-30');
  const [contentLang, setContentLang] = useState('en');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [sRes, logRes] = await Promise.all([
        getMaintenanceDripSettings(),
        getMaintenanceDripSends({ limit: 40 }),
      ]);
      setSettings(sRes.data);
      setSends(logRes.data?.rows || []);
    } catch (e) {
      setSettings(null);
      setSends([]);
      setLoadError(e);
      safeToastError(toast.error, e, 'Failed to load dripping settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const patch = (fn) => setSettings((s) => (s ? fn({ ...s }) : s));

  const settingsDraftPayload = useMemo(() => {
    if (!settings) return null;
    const fromEmail =
      optionalEmailOrNull(settings.from_email) || 'maintenance@evo-home.ch';
    const overdue = ensureOverdueReminders(settings);
    return {
      enabled: !!settings.enabled,
      default_responsible_email: optionalEmailListOrNull(settings.default_responsible_email),
      default_drip_language: settings.default_drip_language || 'en',
      from_name: settings.from_name,
      from_email: fromEmail,
      reply_to: optionalEmailOrNull(settings.reply_to),
      send_hour_local: Number(settings.send_hour_local ?? 9),
      timezone: settings.timezone || 'Europe/Zurich',
      attach_invoice_pdf: settings.attach_invoice_pdf !== false,
      auto_create_bexio_invoice: !!settings.auto_create_bexio_invoice,
      drill_test_email: optionalEmailListOrNull(settings.drill_test_email),
      prep_steps: settings.prep_steps || [],
      invoice_on_issue: settings.invoice_on_issue,
      invoice_drip_steps: settings.invoice_drip_steps || [],
      after_due_steps: settings.after_due_steps || [],
      overdue,
    };
  }, [settings]);

  const settingsSavePayload = useMemo(() => {
    if (!settings) return null;
    const blankToNull = (v) => {
      const s = String(v || '').trim();
      return s || null;
    };
    return {
      ...settingsDraftPayload,
      default_responsible_email: blankToNull(settings.default_responsible_email),
      from_email: String(settings.from_email || '').trim() || 'maintenance@evo-home.ch',
      reply_to: blankToNull(settings.reply_to),
      drill_test_email: blankToNull(settings.drill_test_email),
    };
  }, [settings, settingsDraftPayload]);

  const meta = useMemo(
    () => (settings ? stepMeta(selectedStepKey, settings) : null),
    [settings, selectedStepKey],
  );

  const refreshPreview = useCallback(async () => {
    if (!settingsDraftPayload || !meta) return;
    setPreviewLoading(true);
    try {
      const res = await previewMaintenanceDripEmail({
        language: contentLang,
        phase: meta.phase,
        step_index: meta.stepIndex,
        settings_draft: settingsDraftPayload,
      });
      setPreview(res.data);
    } catch (e) {
      setPreview(null);
      if (e?.response?.status === 422) return;
      safeToastError(toast.error, e, 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  }, [settingsDraftPayload, contentLang, meta]);

  useEffect(() => {
    if (!settings) return undefined;
    const t = window.setTimeout(() => {
      void refreshPreview();
    }, 200);
    return () => window.clearTimeout(t);
  }, [settings, refreshPreview]);

  const selectStep = (key) => {
    setSelectedStepKey(key);
  };

  const selectLang = (lang) => {
    setContentLang(lang);
  };

  // Immediate preview when step or language changes (in addition to debounce on edits).
  useEffect(() => {
    if (!settings) return;
    void refreshPreview();
  }, [selectedStepKey, contentLang]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional immediate refresh on selection

  const save = async () => {
    if (!settingsSavePayload) return;
    setSaving(true);
    try {
      const res = await updateMaintenanceDripSettings(settingsSavePayload);
      setSettings(res.data);
      toast.success('Dripping settings saved');
      await refresh();
    } catch (e) {
      safeToastError(toast.error, e, 'Could not save dripping settings');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async (dryRun) => {
    setRunning(true);
    try {
      const res = await runMaintenanceDripNow({ dry_run: dryRun });
      const s = res.data || {};
      toast.success(
        dryRun
          ? `Dry run: ${s.candidates || 0} prep/overdue candidate(s) across ${s.processed_items || 0} item(s)`
          : `Scheduled drip: sent ${s.sent || 0}, failed ${s.failed || 0}, skipped ${s.skipped || 0}`,
      );
      await refresh();
    } catch (e) {
      safeToastError(toast.error, e, 'Drip run failed');
    } finally {
      setRunning(false);
    }
  };

  const runDrill = async () => {
    if (!String(settings.drill_test_email || '').trim()) {
      toast.error('Set a drill test email first, then save settings.');
      return;
    }
    if (!settingsSavePayload) return;
    setRunning(true);
    try {
      const saved = await updateMaintenanceDripSettings(settingsSavePayload);
      setSettings(saved.data);
      const res = await drillMaintenanceDripNow();
      const s = res.data || {};
      const sent = s.sent || 0;
      const failed = s.failed || 0;
      const candidates = s.candidates || 0;
      if (sent === 0 && failed === 0) {
        toast.message(
          `Drill: nothing sent to ${settings.drill_test_email} (candidates ${candidates}). Check templates / RESEND_API_KEY.`,
        );
      } else if (failed > 0 && sent === 0) {
        toast.error(
          `Drill failed for all ${failed} email(s) to ${settings.drill_test_email}. Check Resend / from address.`,
        );
      } else {
        toast.success(
          `Drill: sent ${sent} to ${settings.drill_test_email} (failed ${failed}, candidates ${candidates})`,
        );
      }
      await refresh();
    } catch (e) {
      safeToastError(toast.error, e, 'Drill failed');
    } finally {
      setRunning(false);
    }
  };

  const updateSelectedCopy = (next) => {
    if (selectedStepKey === 'prep-30' || selectedStepKey === 'prep-7') {
      const days = selectedStepKey === 'prep-7' ? 7 : 30;
      patch((s) => {
        const prep_steps = [...(s.prep_steps || [])];
        const idx = prepIndexForDays(s, days);
        if (!prep_steps[idx]) return s;
        prep_steps[idx] = {
          ...next,
          days_before_next_fee: prep_steps[idx].days_before_next_fee,
          enabled: prep_steps[idx].enabled,
          id: prep_steps[idx].id,
        };
        return { ...s, prep_steps };
      });
      return;
    }
    const overdueIndex = overdueIndexFromKey(selectedStepKey);
    patch((s) => {
      const overdue = ensureOverdueReminders(s);
      const reminders = overdue.reminders.map((r) =>
        Number(r.index) === overdueIndex
          ? {
              ...r,
              ...next,
              index: overdueIndex,
              enabled: r.enabled,
            }
          : r,
      );
      return {
        ...s,
        overdue: { ...overdue, reminders, interval_days: 7, max_reminders: 3 },
      };
    });
  };

  const setSelectedEnabled = (enabled) => {
    if (selectedStepKey === 'prep-30' || selectedStepKey === 'prep-7') {
      const days = selectedStepKey === 'prep-7' ? 7 : 30;
      patch((s) => {
        const prep_steps = [...(s.prep_steps || [])];
        const idx = prepIndexForDays(s, days);
        if (!prep_steps[idx]) return s;
        prep_steps[idx] = { ...prep_steps[idx], enabled };
        return { ...s, prep_steps };
      });
      return;
    }
    const overdueIndex = overdueIndexFromKey(selectedStepKey);
    patch((s) => {
      const overdue = ensureOverdueReminders(s);
      const reminders = overdue.reminders.map((r) =>
        Number(r.index) === overdueIndex ? { ...r, enabled } : r,
      );
      return {
        ...s,
        overdue: {
          ...overdue,
          enabled: enabled ? true : overdue.enabled,
          reminders,
          interval_days: 7,
          max_reminders: 3,
        },
      };
    });
  };

  const setOverdueMasterEnabled = (enabled) => {
    patch((s) => ({
      ...s,
      overdue: { ...ensureOverdueReminders(s), enabled, interval_days: 7, max_reminders: 3 },
    }));
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
        Loading dripping settings…
      </div>
    );
  }

  if (!settings || !meta) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-6">
        <p className="text-sm font-medium text-amber-900">Could not load dripping settings</p>
        <p className="mt-1 text-sm text-amber-800">
          {loadError
            ? 'Check your connection or permissions, then retry.'
            : 'Settings are unavailable right now.'}
        </p>
        <div className="mt-4">
          <Button type="button" variant="outline" onClick={() => void refresh()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const subjectKey = contentLang === 'fr' ? 'subject_fr' : 'subject_en';
  const bodyKey = contentLang === 'fr' ? 'body_fr' : 'body_en';
  const prepMissing = !(settings.prep_steps || []).length;
  const isOverdueStep = selectedStepKey.startsWith('overdue');

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 pb-8">
      <Section
        title="Workflow"
        description="Customer journey for maintenance fee emails. Click an automated step to edit its content below."
      >
        <div className="flex flex-wrap items-center gap-3">
          <Field label="Drip automation" className="w-40">
            <select
              className={inputCls}
              value={settings.enabled ? 'yes' : 'no'}
              onChange={(e) => patch((s) => ({ ...s, enabled: e.target.value === 'yes' }))}
            >
              <option value="yes">Enabled</option>
              <option value="no">Disabled</option>
            </select>
          </Field>
          <Field label="Default language" className="w-40">
            <select
              className={inputCls}
              value={settings.default_drip_language || 'en'}
              onChange={(e) => patch((s) => ({ ...s, default_drip_language: e.target.value }))}
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </Field>
          <Field label="Overdue series" className="w-40">
            <select
              className={inputCls}
              value={settings.overdue?.enabled !== false ? 'yes' : 'no'}
              onChange={(e) => setOverdueMasterEnabled(e.target.value === 'yes')}
            >
              <option value="yes">Enabled</option>
              <option value="no">Disabled</option>
            </select>
          </Field>
          <div className="min-w-0 flex-1 text-[12px] text-gray-500">
            Schedule: prep 30d + 7d · Friendly / Second / Final at +7 / +14 / +21 · exact day · after local send hour.
          </div>
        </div>

        <JourneyFlow
          selectedStepKey={selectedStepKey}
          onSelectStep={selectStep}
          dripEnabled={!!settings.enabled}
        />

        {prepMissing ? (
          <p className="text-sm text-amber-700">
            Canonical prep notices missing — save settings to restore the 30-day and 7-day steps.
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
          <span className="text-[11px] text-gray-500">
            Saves enabled state, delivery options, and email templates.
          </span>
        </div>
      </Section>

      <Section title="Email content" description={`${meta.title} · ${meta.blurb}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500">Step</span>
            <div className="inline-flex flex-wrap gap-1 rounded-lg bg-gray-100 p-0.5">
              {[
                { key: 'prep-30', label: '30d' },
                { key: 'prep-7', label: '7d' },
                { key: 'overdue-1', label: 'Friendly' },
                { key: 'overdue-2', label: 'Second' },
                { key: 'overdue-3', label: 'Final' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => selectStep(tab.key)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-[11px] font-semibold',
                    selectedStepKey === tab.key
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-800',
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Field label="This step enabled" className="w-36">
              <select
                className={inputCls}
                value={meta.enabled ? 'yes' : 'no'}
                onChange={(e) => setSelectedEnabled(e.target.value === 'yes')}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </Field>
            <div>
              <div className="text-xs font-medium text-gray-600">Language</div>
              <div className="mt-1">
                <LangTabs value={contentLang} onChange={selectLang} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            <Field label={`Subject (${contentLang.toUpperCase()})`}>
              <input
                className={inputCls}
                value={meta.value?.[subjectKey] || ''}
                onChange={(e) => updateSelectedCopy({ ...meta.value, [subjectKey]: e.target.value })}
              />
            </Field>
            <Field
              label={`Body (${contentLang.toUpperCase()})`}
              hint={
                !isOverdueStep
                  ? 'Keep this an advance notice — no payment link. Payment belongs on the manual invoice email and overdue reminders.'
                  : 'Tokens: {{domain}}, {{customer}}, {{finance_due_date}}, {{invoice_number}}, {{ar_balance_chf}}, {{overdue_index}}'
              }
            >
              <textarea
                rows={12}
                className={areaCls}
                value={meta.value?.[bodyKey] || ''}
                onChange={(e) => updateSelectedCopy({ ...meta.value, [bodyKey]: e.target.value })}
              />
            </Field>
          </div>

          <div className="flex min-h-[280px] flex-col rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Live preview ({contentLang.toUpperCase()})
                {previewLoading ? ' · updating…' : ''}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void refreshPreview()}
                disabled={previewLoading}
              >
                {previewLoading ? 'Rendering…' : 'Refresh'}
              </Button>
            </div>
            {preview ? (
              <div className="mt-3 min-h-0 flex-1 overflow-auto">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Subject</div>
                <div className="mt-1 text-sm font-medium text-gray-900">{preview.subject || '—'}</div>
                <div className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Body</div>
                <pre className="mt-1 whitespace-pre-wrap font-sans text-sm leading-relaxed text-gray-800">
                  {preview.body || '—'}
                </pre>
              </div>
            ) : (
              <div className="mt-6 text-sm text-gray-500">No preview yet</div>
            )}
          </div>
        </div>
      </Section>

      <Section
        title="Delivery & testing"
        description="Sender, schedule window, and verification tools. Production prep/reminders always use each line’s Reminder recipient email(s) — never a global fallback."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field
            label="Default recipient emails (retired)"
            hint="Not used for Launch or production drip. Historical value is kept but ignored — every send requires Reminder recipient email(s) on the maintenance line."
          >
            <input
              type="text"
              className={cn(inputCls, 'bg-gray-100 text-gray-500')}
              placeholder="(retired — not used for production)"
              value={settings.default_responsible_email || ''}
              readOnly
              disabled
              aria-readonly="true"
            />
          </Field>
          <Field
            label="Drill test inbox"
            hint="Drill saves settings, then sends real emails here with a drill banner. Never marks production steps sent."
          >
            <input
              type="text"
              className={inputCls}
              placeholder="you@evo-home.ch"
              value={settings.drill_test_email || ''}
              onChange={(e) => patch((s) => ({ ...s, drill_test_email: e.target.value }))}
            />
          </Field>
          <Field label="From name">
            <input
              className={inputCls}
              value={settings.from_name || ''}
              onChange={(e) => patch((s) => ({ ...s, from_name: e.target.value }))}
            />
          </Field>
          <Field label="From email (@evo-home.ch)">
            <input
              type="email"
              className={inputCls}
              value={settings.from_email || ''}
              onChange={(e) => patch((s) => ({ ...s, from_email: e.target.value }))}
            />
          </Field>
          <Field label="Reply-to">
            <input
              type="email"
              className={inputCls}
              value={settings.reply_to || ''}
              onChange={(e) => patch((s) => ({ ...s, reply_to: e.target.value }))}
            />
          </Field>
          <Field label="Timezone">
            <input
              className={inputCls}
              value={settings.timezone || 'Europe/Zurich'}
              onChange={(e) => patch((s) => ({ ...s, timezone: e.target.value }))}
            />
          </Field>
          <Field label="Preferred send hour (local)" hint="Scheduler waits until this hour. Manual run below bypasses it.">
            <input
              type="number"
              min={0}
              max={23}
              className={inputCls}
              value={settings.send_hour_local ?? 9}
              onChange={(e) => patch((s) => ({ ...s, send_hour_local: e.target.value }))}
            />
          </Field>
        </div>

        <details className="rounded-lg border border-gray-200 bg-gray-50/80 p-3">
          <summary className="cursor-pointer text-xs font-semibold text-gray-800">
            Advanced invoice settings
          </summary>
          <p className="mt-2 text-[11px] text-gray-500">
            Invoicing is owned by row-level Launch invoice &amp; reminders. These options are secondary.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Attach Bexio invoice PDF on overdue" hint="Prep notices never attach a PDF.">
              <select
                className={inputCls}
                value={settings.attach_invoice_pdf !== false ? 'yes' : 'no'}
                onChange={(e) => patch((s) => ({ ...s, attach_invoice_pdf: e.target.value === 'yes' }))}
              >
                <option value="yes">Yes (overdue reminders)</option>
                <option value="no">No</option>
              </select>
            </Field>
            <Field label="Auto-create Bexio invoice (legacy)" hint="Unused by global drip. Prefer row-level Launch invoice & reminders.">
              <select
                className={inputCls}
                value={settings.auto_create_bexio_invoice ? 'yes' : 'no'}
                onChange={(e) => patch((s) => ({ ...s, auto_create_bexio_invoice: e.target.value === 'yes' }))}
              >
                <option value="no">No</option>
                <option value="yes">Yes (legacy — not used by Run scheduled drip)</option>
              </select>
            </Field>
          </div>
        </details>

        <div className="grid gap-3 border-t border-gray-100 pt-3 sm:grid-cols-2">
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 p-3">
            <div className="text-xs font-semibold text-gray-800">Test</div>
            <p className="mt-1 text-[11px] text-gray-500">
              Safe verification. Dry run sends nothing. Drill emails only the test inbox (logged as Drill in Activity).
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => runNow(true)} disabled={running}>
                Dry run (count only)
              </Button>
              <Button variant="outline" onClick={() => void runDrill()} disabled={running}>
                {running ? 'Running…' : 'Drill to test inbox'}
              </Button>
            </div>
          </div>
          <div className="rounded-lg border border-slate-300 bg-white p-3">
            <div className="text-xs font-semibold text-gray-800">Live send</div>
            <p className="mt-1 text-[11px] text-gray-500">
              Sends currently due prep / Friendly / Second / Final steps for previously launched campaigns only.
              Never creates invoices, enrols items, or backfills missed messages. Not the same as row-level Launch invoice &amp; reminders.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() => runNow(false)}
                disabled={running || !settings.enabled}
              >
                {running ? 'Running…' : 'Run scheduled drip now'}
              </Button>
            </div>
            {!settings.enabled ? (
              <p className="mt-2 text-[11px] text-amber-700">
                Enable drip automation to send live emails. Test actions still work.
              </p>
            ) : null}
          </div>
        </div>
      </Section>

      <Section
        title="Activity"
        description="Recent drip emails. Drill rows are labeled so they stay distinguishable from live production sends (history is kept)."
      >
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Run</th>
                <th className="px-3 py-2 text-left">Domain</th>
                <th className="px-3 py-2 text-left">Phase</th>
                <th className="px-3 py-2 text-left">To</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Subject</th>
              </tr>
            </thead>
            <tbody>
              {sends.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-gray-400">
                    No sends yet
                  </td>
                </tr>
              ) : (
                sends.map((row) => {
                  // Defense: never render existing-Issued Launch skip markers as Live sent emails.
                  if (isExistingInvoiceReuseSkip(row)) return null;
                  const drill = isDrillSend(row);
                  return (
                    <tr key={row.id} className={cn('border-t border-gray-100', drill && 'bg-violet-50/40')}>
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-gray-600">
                        {row.sent_at ? new Date(row.sent_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span
                          className={cn(
                            'inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                            drill ? 'bg-violet-100 text-violet-800' : 'bg-slate-100 text-slate-700',
                          )}
                        >
                          {drill ? 'Drill' : 'Live'}
                        </span>
                      </td>
                      <td className="px-3 py-2">{row.domain_name || '—'}</td>
                      <td className="px-3 py-2 text-xs">{row.phase}</td>
                      <td className="max-w-[160px] truncate px-3 py-2 text-xs" title={row.to_email}>
                        {row.to_email}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span
                          className={
                            row.status === 'sent'
                              ? 'text-emerald-700'
                              : row.status === 'failed'
                                ? 'text-red-700'
                                : 'text-gray-500'
                          }
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="max-w-[280px] truncate px-3 py-2 text-xs text-gray-700" title={row.subject}>
                        {row.subject}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
